import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import {
  useGetDyTemplates,
  useGetInfiniteProductsWithVariations,
  useGetProductCategories,
  useGetProductVariationTypes,
  useParamQuery,
} from 'src/hooks';
import { formatQuery } from 'src/utils';

export const useListProductsPage = () => {
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    data: infiniteProductsData,
    isLoading: isLoadingInfiniteProducts,
    error: errorLoadingInfiniteProducts,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteProductsWithVariations(formatQuery(location.search));

  const { data: templates = [], isLoading: isTemplateLoading } =
    useGetDyTemplates();

  const getNextProductPage = () => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const { onChangeParams, getParamsValue } = useParamQuery({
    sort: [`-${SortOptions.LAST_MODIFIED}`],
  });

  const { data: productCategories = [] } = useGetProductCategories([
    'sort=*name',
  ]);
  const { data: productVariationTypes = [] } = useGetProductVariationTypes([
    'sort=*name',
  ]);

  const selectedSortOption: SortMenuOption = {
    option: getParamsValue('sort')[0]?.replace('-', '') as SortOptions,
    order: getParamsValue('sort')[0]?.includes('-') ? 'desc' : 'asc',
  };

  const onSortChangeHandle = (selectedOption: {
    option: SortOptions;
    order: 'desc' | 'asc';
  }) => {
    onChangeParams('sort', [
      `${selectedOption.order === 'desc' ? '-' : ''}${selectedOption.option}`,
    ]);
  };

  // Scroll to top if search params change
  useEffect(() => {
    if (scrollRef.current && location.search) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [location.search]);

  return {
    productVariationTypes,
    productCategories,
    onChangeParams,
    infiniteProductsData,
    isLoadingInfiniteProducts,
    errorLoadingInfiniteProducts,
    getNextProductPage,
    selectedSortOption,
    onSortChangeHandle,
    templates,
    isTemplateLoading,
    scrollRef,
  };
};
